import { useState } from 'react';
import { Send, XCircle } from 'lucide-react';
import { cancelConsequence } from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import {
  activeTopupPlanFor,
  askOpsToCancel,
  canCancelBooking,
  cancelBooking,
  markNoShowBooking,
} from '@/domain/opsCancelFlow';
import { currentActorId } from '@/domain/actor';
import { aud } from '@/utils/format';
import { depositPaidAud } from '@/utils/bookingSummary';
import { selfPersonIdFromSession } from '@/utils/commsHelpers';
import type { Booking } from '@/domain/types';

export const CANCEL_REASON_CODES: Array<{ code: string; label: string }> = [
  { code: 'customer-requested', label: 'Customer requested' },
  { code: 'artist-unavailable', label: 'Artist unavailable' },
  { code: 'illness', label: 'Illness' },
  { code: 'double-booking', label: 'Double booking' },
  { code: 'other', label: 'Other' },
];

function consequenceError(error: string | undefined): string {
  if (error === 'past-booking') return 'That appointment has passed. Mark it a no-show instead.';
  if (error === 'already-cancelled') return 'That booking is already cancelled.';
  return "That booking can't be cancelled.";
}

/**
 * Cancel (or mark no-show) ONE booking through the one cancel intent, with the
 * shared 7-day split. The reason / consequence / action part of the customer
 * record's Cancel (W5b #1, tv-2na.12.17), shared with the calendar's booking
 * card (B9 calendar-14). A typed reason is required; the consequence line shows
 * the money actually paid (forfeit inside 7 days, kept on file outside). Only Ops
 * cancels (Q4, DECIDED 25 Sep): every other persona gets "Ask Ops to cancel", an
 * Ops DM carrying the booking link. The screen rule matches the customer record;
 * the server check waits for real auth (tv-1vh.13, B9 Q6).
 */
export default function CancelBookingPanel({
  booking,
  mode,
  source,
  onDone,
  onBack,
}: {
  booking: Booking;
  mode: 'cancel' | 'no-show';
  /** The surface raising it, logged on the cancel (customer-record, calendar…). */
  source: string;
  onDone: () => void;
  onBack: () => void;
}) {
  const deposits = useBrainStore((s) => s.deposits);
  const topupPlans = useBrainStore((s) => s.topupPlans);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clock = useSimulationStore((s) => s.clockIso);
  const persona = useSessionStore((s) => s.persona);
  const agentId = useSessionStore((s) => s.agentId);
  const artistId = useSessionStore((s) => s.artistId);
  const apprenticeId = useSessionStore((s) => s.apprenticeId);
  const isOps = canCancelBooking(persona);

  const [reasonCode, setReasonCode] = useState(CANCEL_REASON_CODES[0]!.code);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const deposit = booking.depositId ? deposits[booking.depositId] : undefined;
  const plan = activeTopupPlanFor(topupPlans, booking.id, deposit?.id);
  const paid = deposit ? depositPaidAud(deposit) : 0;
  const consequence =
    mode === 'no-show'
      ? paid > 0
        ? `Marks a no-show. The ${aud(paid)} paid is forfeited to the house.`
        : 'Marks a no-show. No deposit on file.'
      : cancelConsequence({ booking, deposit, plan, atIso: clock }).text;
  const trimmed = reason.trim();
  const by = currentActorId() ?? persona ?? 'ops';

  const onCancel = async () => {
    if (!trimmed || saving) return;
    const label = CANCEL_REASON_CODES.find((r) => r.code === reasonCode)?.label ?? reasonCode;
    const res = cancelBooking({ bookingId: booking.id, reason: trimmed, reasonCode, by, source });
    if (!res.ok) {
      pushNotification({ intensity: 'critical', title: "Couldn't cancel", body: consequenceError(res.error), ttlMs: 6000 });
      return;
    }
    if (res.completion) {
      setSaving(true);
      const committed = await res.completion;
      setSaving(false);
      if (!committed) return;
    }
    pushNotification({
      intensity: 'high',
      title: 'Booking cancelled',
      body: `${label}. ${consequence ?? ''} Slot freed to the Sweeper.`,
      ttlMs: 5000,
    });
    onDone();
  };

  const onNoShow = async () => {
    if (!trimmed || saving) return;
    const res = markNoShowBooking({ bookingId: booking.id, reason: trimmed, by, source });
    if (!res.ok) {
      pushNotification({ intensity: 'critical', title: "Couldn't mark the no-show", body: res.error ?? 'Refused.', ttlMs: 6000 });
      return;
    }
    if (res.completion) {
      setSaving(true);
      const committed = await res.completion;
      setSaving(false);
      if (!committed) return;
    }
    pushNotification({ intensity: 'high', title: 'Marked no-show', body: consequence ?? '', ttlMs: 5000 });
    onDone();
  };

  const onAskOps = () => {
    if (!trimmed) return;
    const from = selfPersonIdFromSession({ persona, agentId, artistId, apprenticeId }) ?? persona ?? 'agent';
    const verb = mode === 'no-show' ? 'Mark as a no-show please.' : '';
    const res = askOpsToCancel({ bookingId: booking.id, fromPersonId: from, note: `${verb} ${trimmed}`.trim() });
    if (res.ok) {
      pushNotification({ intensity: 'info', title: 'Asked Ops to cancel', body: 'Ops has the booking link and your note.', ttlMs: 4000 });
      onDone();
    }
  };

  return (
    <>
      {mode === 'cancel' ? (
        <label className="block mt-3">
          <span className="label-small-caps">Reason</span>
          <select
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            data-testid="cancel-reason-code"
            className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
          >
            {CANCEL_REASON_CODES.map((r) => (
              <option key={r.code} value={r.code}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label className="block mt-3">
        <span className="label-small-caps">{isOps ? 'Why (typed, required)' : 'Note for Ops (required)'}</span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          data-testid="cancel-reason"
          placeholder={isOps ? 'What the client said, in a line' : 'Why they want to cancel'}
          className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
        />
      </label>
      {consequence ? (
        <p className="mt-4 text-xs font-serif text-cream-soft" data-testid="cancel-consequence">
          {isOps ? consequence : `If Ops cancels: ${consequence}`}
        </p>
      ) : null}

      <div className="flex gap-2 mt-5 justify-end">
        <button type="button" onClick={onBack} className="btn-ghost text-xs">
          Back
        </button>
        {!isOps ? (
          <button
            type="button"
            onClick={onAskOps}
            disabled={!trimmed || saving}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-blood/80 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-cream hover:bg-blood disabled:opacity-40"
            data-testid="ask-ops-cancel"
          >
            <Send size={12} /> Ask Ops to cancel
          </button>
        ) : mode === 'no-show' ? (
          <button
            type="button"
            onClick={onNoShow}
            disabled={!trimmed || saving}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-blood/80 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-cream hover:bg-blood disabled:opacity-40"
            data-testid="noshow-confirm"
          >
            <XCircle size={12} /> Mark no-show
          </button>
        ) : (
          <button
            type="button"
            onClick={onCancel}
            disabled={!trimmed || saving}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-blood/80 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-cream hover:bg-blood disabled:opacity-40"
            data-testid="cancel-confirm"
          >
            <XCircle size={12} /> Confirm cancellation
          </button>
        )}
      </div>
    </>
  );
}
